import { InputAdornment, MenuItem, styled, IconButton } from "@mui/material";
import { useTranslation } from "react-i18next";
import EmployeePageButtons from "../employeePageButtons/employeePageButtons";
import "./employeeOrders.css";
import SearchIcon from "@mui/icons-material/Search";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useNavigate, useParams } from "react-router-dom";
import { ButtonUnstyled, buttonUnstyledClasses } from "@mui/base";
import { CacheProvider } from "@emotion/react";
import authStore from "@/stores/authStore";
import rtlPlugin from "stylis-plugin-rtl";
import createCache from "@emotion/cache";
import { prefixer } from "stylis";
import BaseInput from "@/components/common/baseInput";
import BaseDateInput from "@/components/common/baseDateInput";
import BaseButton from "@/components/common/baseButton";
import { useCallback, useEffect, useState } from "react";
import { OrderStatus } from "@/models/enums";
import { checkPermissionsWithAlert, formatAmount, formatDate } from "@/util";
import { EmployeeOrderResponse, EmployeeOrdersFilterRequest, EmployeeOrdersFilterResponse } from "@/models/employees";
import dayjs from "dayjs";
import popupService from "@/services/popupService";
import api from "@/services/api";
import { IdText, PermissionName } from "@/models/apiModels";
import filtersStore from "@/stores/filtersStore";
import SearchProviderAutocomplete from "@/components/orders/search.provider.autocomplete";
import TotalCountComponent from "@/components/common/resultCountComponent";
import ClearIcon from "@/assets/icons/clear-icon.svg";

const cacheLtr = createCache({
    key: "muiltr",
});

const cacheRtl = createCache({
    key: "muirtl",
    stylisPlugins: [prefixer, rtlPlugin],
});

const DetailsButton = styled(ButtonUnstyled)(
    () => `
          &:hover {
            background-color: #222C4B;
            padding: 0px 21px;
          }
        
          &.${buttonUnstyledClasses.active} {
            background-color: #222C4B;
            padding: 0px 21px;
          }

          &.${buttonUnstyledClasses.root} {
            padding: 0px 21px;
          }
        
          &.${buttonUnstyledClasses.focusVisible} {
            box-shadow: 0 3px 20px 0 rgba(61, 71, 82, 0.1), 0 0 0 5px rgba(0, 127, 255, 0.5);
            outline: none;
            padding: 0px 21px;
          }
        
          &.${buttonUnstyledClasses.disabled} {
            opacity: 0.5;
            cursor: not-allowed;
            padding: 0px 21px;
          }`
);

const EmployeeOrders = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { id } = useParams();
    const [orders, setOrders] = useState<EmployeeOrderResponse[]>([]);
    const [employeeOrderFilter, setEmployeeOrderFilter] = useState<EmployeeOrdersFilterRequest>({
        keywords: "",
        fromDate: null,
        tillDate: null,
        orderStatus: null,
        providerName: "",
        employeeId: Number(id),
        orderBy: "",
        asc: false,
        pageIndex: 0,
    });
    const [hasNext, setHasNext] = useState(true);
    const [searchCounter, setSearchCounter] = useState(0);
    const [total, setTotal] = useState(0);
    const [totalFilter, setTotalFilter] = useState(0);

    const orderStatusList: IdText[] = (
        Object.values(OrderStatus).filter((val) => isNaN(Number(val))) as (keyof typeof OrderStatus)[]
    ).map((key) => new IdText(OrderStatus[key].toString(), key));

    function handleClearFilters() {
        filtersStore.setEmployeeOrderFilterData(null);
        setEmployeeOrderFilter((prevData) => ({
            ...prevData,
            keywords: "",
            fromDate: null,
            tillDate: null,
            orderStatus: null,
            orderBy: "",
            asc: false,
            providerName: "",
        }));
        setSearchCounter(searchCounter + 1);
    }

    function handleOnChangeFromDate(e: any) {
        const date = dayjs(e?.toString());
        const formattedDate = date.isValid() ? date.format("YYYY-MM-DD") : "";
        setEmployeeOrderFilter((prevData) => ({
            ...prevData,
            fromDate: e ? formattedDate : null,
        }));
    }

    function handleOnChangeTillDate(e: any) {
        const date = dayjs(e?.toString());
        const formattedDate = date.isValid() ? date.format("YYYY-MM-DD") : "";
        setEmployeeOrderFilter((prevData) => ({
            ...prevData,
            tillDate: e ? formattedDate : null,
        }));
    }

    async function onSearchClick() {
        setOrders([]);
        popupService.showLoading();
        filtersStore.setEmployeeOrderFilterData(null);
        setEmployeeOrderFilter((prevData) => ({
            ...prevData,
            pageIndex: 0,
        }));
        setSearchCounter(searchCounter + 1);
    }

    async function getEmployeeOrders(): Promise<EmployeeOrdersFilterResponse> {
        const filterStore = filtersStore.employeesOrderFilterData;
        if (filterStore != null && filterStore.employeeId === Number(id)) {
            if (filterStore.pageIndex !== 0) {
                filterStore.pageIndex = 0;
            }
            setEmployeeOrderFilter(filterStore ?? new EmployeeOrdersFilterRequest());
            const responsePromise = api.getEmployeeOrders(filterStore ?? new EmployeeOrdersFilterRequest());
            return (await responsePromise).data;
        }

        filtersStore.setEmployeeOrderFilterData(employeeOrderFilter);
        const responsePromise = api.getEmployeeOrders(employeeOrderFilter);
        return (await responsePromise).data;
    }

    const handleScroll = useCallback(() => {
        const ordersContainer = document.getElementById("employee-orders-list-grid");
        const doScroll =
            ordersContainer &&
            ordersContainer.scrollTop + ordersContainer.clientHeight >= ordersContainer.scrollHeight &&
            ordersContainer.scrollTop > 0;

        if (!doScroll || !hasNext) {
            return;
        }
        filtersStore.setEmployeeOrderFilterData(null);
        setEmployeeOrderFilter((prevData) => ({
            ...prevData,
            pageIndex: prevData.pageIndex + 1,
        }));
    }, [employeeOrderFilter.pageIndex, hasNext]);

    let startFetchingBusy = false;
    async function startFetching() {
        if (!checkPermissionsWithAlert(PermissionName.EmployeeOrders)) {
            return;
        }
        if (startFetchingBusy) {
            return;
        }
        try {
            startFetchingBusy = true;
            popupService.showLoading();
            const data = await getEmployeeOrders();
            const apiResponse = data;
            setHasNext(apiResponse.hasNext);
            if (employeeOrderFilter.pageIndex === 0) {
                setOrders(apiResponse.rows);
                setTotal(apiResponse.total);
                setTotalFilter(apiResponse.totalFilter);
            } else {
                setOrders((oldArray) => [...oldArray, ...apiResponse.rows]);
            }
            popupService.close();
        } catch (err) {
            popupService.showApiError(err);
            return;
        } finally {
            startFetchingBusy = false;
        }
    }

    function navigateToOrderDetails(order: EmployeeOrderResponse) {
        navigate(`/employees/${id}/employeeOrders/${order.id}`, { state: { docketNumber: order.docket } });
    }

    async function sortBy(sortBy: string) {
        if (employeeOrderFilter.orderBy !== sortBy) {
            setEmployeeOrderFilter((prevData) => ({
                ...prevData,
                asc: true,
                orderBy: sortBy,
                pageIndex: 0,
            }));
        } else {
            setEmployeeOrderFilter((prevData) => ({
                ...prevData,
                asc: !employeeOrderFilter.asc,
                orderBy: sortBy,
                pageIndex: 0,
            }));
        }
        setOrders([]);
        filtersStore.setEmployeeOrderFilterData(null);
    }

    function getArrowClass(sort: string) {
        if (sort === employeeOrderFilter.orderBy) {
            if (employeeOrderFilter.asc) {
                return "top-sort-arrow";
            } else {
                return "bottom-sort-arrow";
            }
        }
        return "icon-SortArrowIcon sort-arrow-icon";
    }

    useEffect(() => {
        let ignore = false;
        if (!ignore) {
            startFetching();
        }
        return () => {
            ignore = true;
        };
    }, [employeeOrderFilter.pageIndex, employeeOrderFilter.orderBy, employeeOrderFilter.asc, searchCounter]);

    useEffect(() => {
        const ordersContainer = document.getElementById("employee-orders-list-grid");
        if (!ordersContainer) {
            return;
        }
        ordersContainer.addEventListener("scroll", handleScroll);
        return () => {
            ordersContainer.removeEventListener("scroll", handleScroll);
        };
    }, [handleScroll]);

    function handleKeyPressSearch(event: any) {
        if (event.key === "Enter") {
            onSearchClick();
        }
    }

    useEffect(() => {
        document.addEventListener("keypress", handleKeyPressSearch);
        return () => {
            document.removeEventListener("keypress", handleKeyPressSearch);
        };
    });

    return (
        <CacheProvider value={authStore.isRtl ? cacheRtl : cacheLtr}>
            {checkPermissionsWithAlert(PermissionName.EmployeeOrders) ? (
                <div className="employee-orders-container">
                    <div className="employee-orders-header">
                        <EmployeePageButtons />
                        <div className="employee-orders-filter">
                            <div className="font-style employee-orders-span">{t("employee.FilterHeader")}</div>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}>
                                <div className="emp-digital-wallet-filter-row">
                                    <div>
                                        <BaseInput
                                            id="keyword-input"
                                            className="search-input"
                                            placeholder={`${t("orders.Employee.SearchBy")}`}
                                            type="search"
                                            size="small"
                                            value={employeeOrderFilter.keywords}
                                            onChange={(event) =>
                                                setEmployeeOrderFilter((prevData) => ({
                                                    ...prevData,
                                                    keywords: event.target.value,
                                                }))
                                            }
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <SearchIcon />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                width: "290px",
                                            }}
                                        />
                                    </div>
                                    <div style={{ width: 160 }}>
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <DesktopDatePicker
                                                className="date-picker-input"
                                                label={t("orders.Employee.FromDate")}
                                                inputFormat="DD/MM/YYYY"
                                                value={employeeOrderFilter?.fromDate}
                                                onChange={(event) => handleOnChangeFromDate(event)}
                                                renderInput={(params) => <BaseDateInput {...params} />}
                                            />
                                        </LocalizationProvider>
                                    </div>
                                    <div style={{ width: 160 }}>
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <DesktopDatePicker
                                                className="date-picker-input"
                                                label={t("orders.Employee.TillDate")}
                                                inputFormat="DD/MM/YYYY"
                                                value={employeeOrderFilter?.tillDate}
                                                onChange={(event) => handleOnChangeTillDate(event)}
                                                renderInput={(params) => <BaseDateInput {...params} />}
                                            />
                                        </LocalizationProvider>
                                    </div>
                                    <div>
                                        <BaseInput
                                            select
                                            id="orderStatus-input"
                                            label={t("orders.orderStatus")}
                                            size="small"
                                            value={employeeOrderFilter.orderStatus || ""}
                                            onChange={(event: any) =>
                                                setEmployeeOrderFilter((prevData) => ({
                                                    ...prevData,
                                                    orderStatus: event.target.value,
                                                }))
                                            }
                                            sx={{
                                                width: "160px",
                                            }}
                                            InputProps={{
                                                endAdornment: (
                                                    <IconButton
                                                        className="select-clear-icon"
                                                        sx={{
                                                            visibility: employeeOrderFilter.orderStatus
                                                                ? "visible"
                                                                : "hidden",
                                                        }}
                                                        onClick={() =>
                                                            setEmployeeOrderFilter((prevData) => ({
                                                                ...prevData,
                                                                orderStatus: null,
                                                            }))
                                                        }
                                                    >
                                                        <img src={ClearIcon} alt="Clear icon" />
                                                    </IconButton>
                                                ),
                                            }}
                                        >
                                            {orderStatusList?.map((orderStatus) => (
                                                <MenuItem key={orderStatus.id} value={orderStatus.text}>
                                                    {orderStatus.text}
                                                </MenuItem>
                                            ))}
                                        </BaseInput>
                                    </div>
                                    <div>
                                        <SearchProviderAutocomplete
                                            value={employeeOrderFilter.providerName}
                                            updateProvider={(args: string | null) => {
                                                setEmployeeOrderFilter(() => ({
                                                    ...employeeOrderFilter,
                                                    providerName: args || null,
                                                }));
                                            }}
                                            width="260px"
                                        ></SearchProviderAutocomplete>
                                    </div>
                                </div>
                                <div
                                    style={{
                                        display: "flex",
                                        justifyContent: "flex-end",
                                        gap: 15,
                                        marginInlineStart: 10,
                                    }}
                                >
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "flex-end",
                                            minWidth: "70px",
                                        }}
                                    >
                                        <span
                                            onClick={handleClearFilters}
                                            className="link-element clear-all-btn"
                                            style={{
                                                minWidth: "70px",
                                            }}
                                        >
                                            {t("display.ClearAll")}
                                        </span>
                                    </div>
                                    <BaseButton
                                        style={{ padding: "0px 25px" }}
                                        className="basic-button"
                                        onClick={() => onSearchClick()}
                                    >
                                        {t("general.filter")}
                                    </BaseButton>
                                </div>
                            </div>
                        </div>
                    </div>
                    <TotalCountComponent
                        title={t("employees.QuantityOfOrders") as string}
                        allCount={total}
                        currentCount={totalFilter}
                    />
                    <div className="employee-orders-content">
                        <div id="employee-orders-list-grid" className="employee-orders-list-grid">
                            <div className="employee-orders-fake-thead"></div>
                            <table>
                                <thead>
                                    <tr>
                                        <td>{t("orders.externalSystem")}</td>
                                        <td onClick={() => sortBy("OrderNumber")}>
                                            <div className="table-number-format-name">
                                                <span>
                                                    {t("orders.orderNumber")}
                                                    <i className={`icon-f ${getArrowClass("OrderNumber")} fs-12`} />
                                                </span>
                                            </div>
                                        </td>
                                        <td onClick={() => sortBy("OrderStatus")}>
                                            <span>
                                                {t("orders.orderStatus")}
                                                <i className={`icon-f ${getArrowClass("OrderStatus")} fs-12`} />
                                            </span>
                                        </td>
                                        <td onClick={() => sortBy("OrderType")}>
                                            <span>
                                                {t("orders.orderType")}
                                                <i className={`icon-f ${getArrowClass("OrderType")} fs-12`} />
                                            </span>
                                        </td>
                                        <td onClick={() => sortBy("CreationDate")}>
                                            <div className="table-number-format-name">
                                                <span>
                                                    {t("orders.creationDate")}
                                                    <i className={`icon-f ${getArrowClass("CreationDate")} fs-12`} />
                                                </span>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="table-number-format-name">{t("orders.orderTotal")}</div>
                                        </td>
                                        <td onClick={() => sortBy("Provider")}>
                                            <span>
                                                {t("orders.serviceProvider")}
                                                <i className={`icon-f ${getArrowClass("Provider")} fs-12`} />
                                            </span>
                                        </td>
                                        <td>
                                            <div className="table-number-format-name">{t("orders.subsidyAmount")}</div>
                                        </td>
                                        <td>
                                            <div className="table-number-format-name">{t("orders.paidByEmployee")}</div>
                                        </td>
                                        <td>{t("orders.details")}</td>
                                    </tr>
                                </thead>
                                <tbody>
                                    {orders?.map((order) => (
                                        <tr key={order.id}>
                                            <td>{order.externalSystem}</td>
                                            <td>
                                                <div
                                                    className="link-element table-number-format-value"
                                                    onClick={() => navigateToOrderDetails(order)}
                                                >
                                                    {order.docket}
                                                </div>
                                            </td>
                                            <td>
                                                <div className="employee-status">
                                                    <div
                                                        className={
                                                            order.status.toString() === OrderStatus[OrderStatus.OK]
                                                                ? "status-color-active"
                                                                : "status-color-inactive"
                                                        }
                                                    ></div>
                                                    <div>{order.status}</div>
                                                </div>
                                            </td>
                                            <td>{t(`enums.OrderType.${order.type}`)}</td>
                                            <td>
                                                <div className="table-number-format-value">
                                                    {formatDate(order.createdAt)}
                                                </div>
                                            </td>
                                            <td>
                                                <div className="table-number-format-value">
                                                    {formatAmount(order.total, order.currencyId)}
                                                </div>
                                            </td>
                                            <td>{order.provider}</td>
                                            <td>
                                                <div className="table-number-format-value">
                                                    {formatAmount(order.subsidyAmount, order.currencyId)}
                                                </div>
                                            </td>
                                            <td>
                                                <div className="table-number-format-value">
                                                    {formatAmount(order.paidByEmployee, order.currencyId)}
                                                </div>
                                            </td>
                                            <td>
                                                <DetailsButton
                                                    onClick={() => navigateToOrderDetails(order)}
                                                    className="details-button"
                                                >
                                                    {t("orders.details")}
                                                </DetailsButton>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            ) : (
                ""
            )}
        </CacheProvider>
    );
};

export default EmployeeOrders;
